import { HttpRequest, HttpEvent, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { NEVER, Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { LoginService } from '../services/login.service';
import { HANDLED_RESULT_STATUSES } from './error.interceptor';

/** Endpoints of the login flow itself: a 401 there is an answer for that screen, never an expired session. */
const AUTH_ENDPOINT = /\/api\/auth(?:[/?]|$)/i;

/**
 * The `[Authorize]` actions of `AuthController`, used by an open session (profile, 2FA management): their 401
 * means the session is gone, like any other API call. Wrong-password answers there are 400, not 401.
 */
const PROTECTED_AUTH_ENDPOINT = /\/api\/auth\/(?:qr|apiqr|userinfo|changepassword|webauthn\/credentials?(?:\/[^/?]*)?)(?:[?]|$)/i;

function isLoginFlowEndpoint(url: string): boolean {
    return AUTH_ENDPOINT.test(url) && !PROTECTED_AUTH_ENDPOINT.test(url);
}

export function authInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    const loginService = inject(LoginService);

    const isApiRequest = req.url.includes('/api/');
    const isLogoutRequest = req.url.includes('Auth/Logout');

    // Skip auth for login/refresh/external endpoints
    if (req.url.includes('Auth/Refresh') ||
        isLogoutRequest ||
        req.url.endsWith('/Auth') ||
        req.url.includes('auth0.com') ||
        req.url.includes('getbee.io') ||
        req.url.includes('presidencia.gob.es')) {
        return next(req);
    }

    // Add language header
    const language = loginService.get('language') || 'ES';
    let headers = req.headers.set('language-code', language);

    // Check if token needs refresh (cookie-based auth)
    if (loginService.shouldRefreshToken(req.url)) {
        return from(loginService.refresh()).pipe(
            switchMap(() => {
                const authReq = req.clone({ headers, withCredentials: isApiRequest });
                return next(authReq);
            }),
            catchError((error: unknown) => handleSessionError(req, error, isApiRequest, loginService))
        );
    }

    const authReq = req.clone({ headers, withCredentials: isApiRequest });
    return next(authReq).pipe(
        catchError((error: unknown) => handleSessionError(req, error, isApiRequest, loginService))
    );
}

/**
 * A 401 on an authenticated API request means the session is no longer valid (expired, or signed with a key the
 * server no longer accepts): close it once and go to the login. The request never settles, so no screen shows a
 * generic error toast for it; the page reload that `closeSession` triggers discards it. Once the session is closing,
 * every other failure is swallowed the same way.
 */
function handleSessionError(
    req: HttpRequest<unknown>,
    error: unknown,
    isApiRequest: boolean,
    loginService: LoginService
): Observable<never> {
    if (loginService.isSessionClosing()) {
        return NEVER;
    }

    if (error instanceof HttpErrorResponse &&
        error.status === 401 &&
        isApiRequest &&
        !isLoginFlowEndpoint(req.url) &&
        !req.context.get(HANDLED_RESULT_STATUSES).includes(401) &&
        loginService.isLoggedIn()) {
        loginService.closeSession();
        return NEVER;
    }

    return throwError(() => error);
}
